import { useState, useEffect } from 'react'
import type { Session } from '@supabase/supabase-js'
import { apiFetch, alertsApi } from '../services/api'
import { supabase } from '../services/supabase'
import { reportError } from '../utils/errors'
import { capture } from '../services/posthog'

interface Props {
  session: Session
}

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  pro_1: 'Pro Base',
  pro_2: 'Pro Plus',
}

export default function Settings({ session }: Props) {
  const user = session.user
  const email = user.email ?? ''
  const memberSince = new Date(user.created_at).toLocaleDateString('it-IT', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  const [limitInfo, setLimitInfo] = useState<{ limit: number; count: number } | null>(null)
  const [upgradeInterest, setUpgradeInterest] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [exportError, setExportError] = useState('')

  useEffect(() => {
    alertsApi.getLimit(session).then(setLimitInfo).catch(() => {})
  }, [session])

  async function handleExport() {
    setExporting(true)
    setExportError('')
    try {
      const data = await apiFetch<unknown>('/account/export', session)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'radar-concorsi-export.json'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setExportError(reportError(e))
    } finally {
      setExporting(false)
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true)
    try {
      await apiFetch<void>('/account', session, { method: 'DELETE' })
      await supabase.auth.signOut()
      window.location.href = '/'
    } catch (e) {
      setDeleting(false)
      setConfirmDelete(false)
      setDeleteError(reportError(e))
    }
  }

  const planKey = (user.user_metadata?.plan as string) ?? 'free'
  const planLabel = PLAN_LABELS[planKey] ?? 'Free'

  return (
    <div className="max-w-2xl mx-auto px-6 py-10 space-y-10">
      <h1 className="text-2xl font-bold">Impostazioni</h1>

      {/* ── Account info ── */}
      <section className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-100">
        <div className="px-6 py-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Account</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Email</span>
              <span className="font-medium text-gray-900">{email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Membro dal</span>
              <span className="font-medium text-gray-900">{memberSince}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Piano ── */}
      <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="px-6 py-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Piano</h2>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{planLabel}</span>
                {planKey === 'free' && (
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Gratuito</span>
                )}
                {planKey !== 'free' && (
                  <span className="text-xs px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full">Attivo</span>
                )}
              </div>
              {limitInfo && (
                <p className="text-xs text-gray-400">
                  {limitInfo.count} / {limitInfo.limit} avvisi attivi
                </p>
              )}
            </div>
            {planKey === 'free' && (
              upgradeInterest ? (
                <span className="text-sm text-green-600 font-medium">✓ Ti avviseremo!</span>
              ) : (
                <button
                  onClick={() => { capture('upgrade_interest_clicked', { source: 'settings' }); setUpgradeInterest(true) }}
                  className="text-sm px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white transition-colors"
                >
                  Passa a Pro
                </button>
              )
            )}
          </div>
        </div>
      </section>

      {/* ── Dati personali ── */}
      <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="px-6 py-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Dati personali</h2>
          <p className="text-xs text-gray-400 mb-4">
            Scarica una copia di tutti i tuoi dati: avvisi salvati e corrispondenze trovate.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {exporting ? 'Esportazione…' : 'Esporta dati (JSON)'}
          </button>
          {exportError && <p className="text-sm text-gray-600 mt-2">{exportError}</p>}
        </div>
      </section>

      {/* ── Zona pericolosa ── */}
      <section className="bg-white rounded-xl border border-red-100 shadow-sm">
        <div className="px-6 py-4">
          <h2 className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-1">Zona pericolosa</h2>
          <p className="text-xs text-gray-400 mb-4">
            L'eliminazione è permanente: account, avvisi e corrispondenze verranno cancellati immediatamente.
          </p>

          {deleteError && <p className="text-sm text-gray-600 mb-3">{deleteError}</p>}
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-sm px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              Elimina account
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-700">Sei sicuro? Questa azione non è reversibile.</p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Eliminazione…' : 'Sì, elimina'}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
